import {
  defineAsyncComponent,
  type AsyncComponentLoader,
  type Component,
} from "vue";
import AsyncLoadError from "./AsyncLoadError.vue";
import LoadingSpinner from "./LoadingSpinner.vue";

/** Total number of load attempts, including the first one. */
const MAX_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 500;

/**
 * `defineAsyncComponent` with the app's shared handling for lazily loaded
 * chunks: a dynamic import that fails usually means the connection dropped for
 * a moment, so retry a few times before showing anything to the user.
 */
export function defineLazyComponent<T extends Component>(
  loader: AsyncComponentLoader<T>,
  fallbacks: {
    loadingComponent?: Component | null;
    errorComponent?: Component | null;
  } = {},
) {
  const { loadingComponent = LoadingSpinner, errorComponent = AsyncLoadError } =
    fallbacks;

  return defineAsyncComponent<T>({
    loader,
    loadingComponent: loadingComponent ?? undefined,
    errorComponent: errorComponent ?? undefined,
    onError(_error, retry, fail, attempts) {
      // `attempts` is 1 on the first failure.
      if (attempts < MAX_ATTEMPTS) {
        setTimeout(retry, RETRY_BASE_DELAY_MS * 2 ** (attempts - 1));
        return;
      }
      fail();
    },
  });
}
